import { Address } from "@certn/cascadia/patterns/address-form/types";
import { ComponentType, ReactNode } from "react";
import { JsonFormsCore } from "@jsonforms/core";
import { RefObject, createContext, useContext } from "react";

import { ApplicationSchema, SectionSchema } from "./types";

interface Props {
  schema: JsonFormsCore["schema"];
  uischema?: ApplicationSchema;
  data: JsonFormsCore["data"];
  currentSection: string;
  completedSections?: string[];
  invalidSections?: string[];
  isSubmitting?: boolean;
  nextSection?: string | SectionSchema;
  prevSection?: string | SectionSchema;
  refresh: () => Promise<void> | void;
  navigateToSection: (
    section: string | SectionSchema,
    type?: "replace" | "navigate"
  ) => void;
  saveData: (
    values: JsonFormsCore["data"],
    validate?: boolean
  ) => Promise<JsonFormsCore["data"]>;
  partiallySaveData: () => Promise<void>;
  submitApplication: () => Promise<void>;
  onChange: (values: JsonFormsCore["data"]) => void;
  customerName?: string;
  clientGroupName?: string;
  clientGroupAddress?: Address;
  onlyFulfillWithDemoResponses?: boolean;
  incompleteSectionSummaryRef?: RefObject<HTMLDivElement>;
  appEnvironment?: string;
  locale?: string;
  branding?: {
    logo_url?: string | undefined;
    org_logo_url_dark_mode?: string | undefined;
    org_icon_url?: string | undefined;
    org_icon_url_dark_mode?: string | undefined;
  };
  /**
   * Custom component to render for the summary section.
   * If not provided, uses the default ApplicationSummary.
   */
  summaryComponent?: ComponentType;
  /**
   * Additional sections to render in ApplicationSectionList after the summary section.
   * Useful for app-specific sections like payment-details.
   */
  additionalSections?: ReactNode;
  /**
   * The base path for routing (e.g., 'apply', 'quickscreen').
   * Optional, defaults to 'apply' if not provided.
   */
  basePath?: string;
}

export const ApplicationContext = createContext<Props>({
  schema: {},
  data: {},
  currentSection: "summary",
  refresh: () => Promise.resolve(),
  navigateToSection: () => {},
  saveData: () => Promise.resolve(),
  partiallySaveData: () => Promise.resolve(),
  submitApplication: () => Promise.resolve(),
  onChange: () => {},
  customerName: "",
  clientGroupName: "",
  clientGroupAddress: undefined,
  appEnvironment: undefined,
  branding: {
    logo_url: undefined,
    org_logo_url_dark_mode: undefined,
    org_icon_url: undefined,
    org_icon_url_dark_mode: undefined,
  },
  summaryComponent: undefined,
  additionalSections: undefined,
  basePath: "apply",
});

export function useApplication() {
  return useContext(ApplicationContext);
}
